import { useQuery } from '@tanstack/react-query';
import { QueryKeys } from 'mode-shared/constants/queryKeys';
import { useAccount, usePublicClient } from 'wagmi';
import { mode } from 'wagmi/chains';
import VotingEscrow from 'mode-shared/constants/abis/VotingEscrow.json';
import { BaseError, ContractFunctionRevertedError, formatUnits, PublicClient } from 'viem';

/** @deprecated */
export interface FundsRecord {
  Address: string;
  ModeToken: number;
  ModeTokenCoefficient: number;
  ModeTokenPrice: number;
  TotalTvl: number;
  BalancerToken: number;
  BalancerTokenPrice: number;
}

const fetcher = async ({
  owner,
  publicClient,
  contractAddress,
}: {
  owner?: `0x${string}`;
  contractAddress?: `0x${string}`;
  publicClient?: PublicClient;
}) => {
  if (!owner || !publicClient || !contractAddress) return;

  try {
    const ownedTokensIds = (await publicClient.readContract({
      address: contractAddress,
      abi: VotingEscrow,
      functionName: 'ownedTokens',
      args: [owner.toLowerCase()],
    })) as string[];

    const lockedTokens = await Promise.all(
      ownedTokensIds.map(async (tokenId) => {
        const lockedToken = (await publicClient.readContract({
          address: contractAddress,
          abi: VotingEscrow,
          functionName: 'locked',
          args: [tokenId],
        })) as { amount: bigint };
        return { tokenId, lockedToken };
      })
    );

    const totalLockedAmount = lockedTokens.reduce((total, token) => {
      const amount = parseFloat(formatUnits(token?.lockedToken?.amount, 18));
      return total + amount;
    }, 0);

    return totalLockedAmount;
  } catch (e) {
    console.error(e);
    if (e instanceof BaseError) {
      const revertError = e.walk((err) => err instanceof ContractFunctionRevertedError);
      if (revertError instanceof ContractFunctionRevertedError) {
        return null;
      }
    }

    return null;
  }
};

export const useStakedModeTokens = (options = {}) => {
  const publicClient = usePublicClient({ chainId: mode.id });
  const { address: owner } = useAccount();
  const contractAddress = '0xff8AB822b8A853b01F9a9E9465321d6Fe77c9D2F';

  return useQuery<number | null | undefined>({
    queryKey: [QueryKeys.STAKED_MODE_TOKENS, !!publicClient],
    queryFn: async () => {
      return fetcher({ owner, publicClient, contractAddress });
    },
    staleTime: 10000,
    enabled: !!owner && !!publicClient,
    ...options,
  });
};

/** @deprecated */
export const useStakedBPTTokens = (options = {}) => {
  const publicClient = usePublicClient();
  const { address: owner } = useAccount();
  const contractAddress = '0x9c2eFe2a1FBfb601125Bb07a3D5bC6EC91F91e01';

  return useQuery<number | null | undefined>({
    queryKey: [QueryKeys.STAKED_BPT_TOKENS, !!publicClient],
    queryFn: async () => {
      return fetcher({ owner, publicClient, contractAddress });
    },
    staleTime: 10000,
    enabled: !!owner && !!publicClient,
    ...options,
  });
};
